import { ActivatedRoute } from '@angular/router';
import { environment } from '@app-environment';
import { ConfigService } from '@shared-lib/foundry-shared/config.service';
import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeycloakService } from 'keycloak-angular';
import { WorkflowTopbarComponent } from '@shared-lib/global-components/workflow-topbar/workflow-topbar.component';
import { SidePanelCardListComponent } from '@shared-lib/global-components/side-panel-card-list/side-panel-card-list.component';
import { DeamEntity, EntityDetailAccordionComponent } from '@shared-lib/global-components/entity-detail-accordion/entity-detail-accordion.component';
import { CatalogNavItem } from '@shared-lib/models/domain-catalog.models';

interface EntityStoreRecord { uuid: string; is_enabled?: boolean; payload?: Record<string, any> | Array<Record<string, any>> | null; }
interface EntitySource { uuid: string; definition: Record<string, any>; saved: boolean; }

@Component({ selector: 'app-deam-service', standalone: true, imports: [CommonModule, WorkflowTopbarComponent, SidePanelCardListComponent, EntityDetailAccordionComponent], templateUrl: './deam-service.component.html', styleUrl: './deam-service.component.scss', changeDetection: ChangeDetectionStrategy.OnPush })
export class DeamServiceComponent implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly route = inject(ActivatedRoute);
  private screenConfig: any;
  private readonly keycloakService = inject(KeycloakService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entitySources = new Map<string, EntitySource>();
  @ViewChild(EntityDetailAccordionComponent) detail?: EntityDetailAccordionComponent;
  entities: DeamEntity[] = [];
  selectedId: string | null = null;
  sidePanelItems: CatalogNavItem[] = [];
  sidebarCollapsed = false;
  loading = false;
  loadError = '';
  toastMessage = '';
  toastVisible = false;
  private toastTimer?: ReturnType<typeof setTimeout>;
  saveStatus: 'idle' | 'saving' | 'saved' = 'idle';
  topbar = { brand: { text: 'SPRICED', width: '60px' }, parentLabel: 'Home', parentRoute: 'spriced-home', currentLabel: 'DEAM Service', showBack: true, backRoute: 'spriced-home', topbarHeight: '52px', reserveSpace: true, zIndex: 9000, glassmorphism: true };

  constructor() {
    this.document.documentElement.classList.add('dashboard-top-navbar-active');
    this.document.body.classList.add('dashboard-top-navbar-active');
    this.loadScreenConfig();
  }
  ngOnDestroy(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.document.documentElement.classList.remove('dashboard-top-navbar-active');
    this.document.body.classList.remove('dashboard-top-navbar-active');
  }

  private loadScreenConfig(): void {
    this.loading = true;
    this.loadError = '';
    const configFile = this.route.snapshot.data['configFile'] || 'assets/foundry-screens/spriced/deam-service.json';
    this.configService.getDashboardConfig(configFile, 'deam-service')
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: config => {
          this.screenConfig = config;
          this.topbar = { ...this.topbar, ...config.components?.workflowTopbarComponent };
          this.loadEntities();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Unable to load the DEAM screen configuration. Please try again.';
          this.changeDetector.markForCheck();
        },
      });
  }

  loadEntities(): void {
    if (!this.screenConfig) { this.loadScreenConfig(); return; }
    const listConfig = this.screenConfig.components?.sidePanelCardListComponent;
    if (!listConfig?.apiUrl || !listConfig.apiPayload) {
      this.loading = false;
      this.loadError = 'The entity list API URL or request payload is missing from the DEAM configuration.';
      this.changeDetector.markForCheck();
      return;
    }
    this.loading = true;
    this.loadError = '';
    this.http.post<EntityStoreRecord[] | EntityStoreRecord>(
      this.resolveApiUrl(listConfig.apiUrl),
      listConfig.apiPayload,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        this.entitySources.clear();
        this.entities = (Array.isArray(response) ? response : [response])
          .filter(record => record && record.is_enabled !== false)
          .flatMap(record => (Array.isArray(record.payload) ? record.payload : record.payload ? [record.payload] : [])
            .filter(definition => !!definition?.['entity']).map((definition, index) => {
            const id = `${record.uuid}:${index}`;
            this.entitySources.set(id, { uuid: record.uuid, definition: structuredClone(definition), saved: true });
            return this.normalizeEntity(definition, id);
          }));
        this.selectedId = this.entities[0]?.id ?? null;
        this.refreshCards();
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      error: () => { this.loading = false; this.loadError = 'Unable to load entities. Please try again.'; this.changeDetector.markForCheck(); },
    });
  }

  private normalizeEntity(raw: Record<string, any>, id: string): DeamEntity {
    const rules = raw['businessRules'];
    return {
      ...raw, id, entity: raw['entity'], displayName: raw['displayName'] ?? '', description: raw['description'] ?? '',
      category: raw['category'] ?? '', entityType: raw['entityType'] ?? 'NonTimeSeries', primaryKey: raw['primaryKey'] ?? '',
      primaryKeyType: raw['primaryKeyType'] ?? 'string', primaryKeyAutogenerated: raw['primaryKeyAutogenerated'] ?? false,
      dataVersionEnabled: raw['dataVersionEnabled'] ?? false, versioningType: raw['versioningType'] ?? 'timebased',
      maxVersion: raw['maxVersion'] ?? 10, isBusinessEntity: raw['isBusinessEntity'] ?? false,
      dataObjectKey: raw['dataObjectKey'] ?? '', isEnabled: raw['isEnabled'] ?? true,
      attributesList: (raw['attributesList'] ?? []).map((a: any, index: number) => ({
        displayName: '', subType: '', mandatory: false, unique: false, indexed: false, businessKey: false,
        enableUpdate: true, versioned: false, maxLength: null, precision: null, scale: null, dateformat: '', staticString: '',
        referencedEntity: null, referencedAttribute: null, statusManagement: null,
        ...a, id: `${id}:attribute:${index}`, expanded: false,
        autoGenerated: a.autogenerated ?? a.autoGenerated ?? false,
        enableAttribute: a.enableAttribute ?? a.enabled ?? true, allowNull: a.allowNull ?? !a.mandatory,
        validValues: (a.validValues ?? []).map(String), compositeUnique: a.compositeUnique ?? [],
      })),
      deamReads: (raw['deamReads'] ?? []).map((r: any, index: number) => ({
        ...r, id: `${id}:read:${index}`, entityName: r.entityName ?? raw['entity'],
        filters: (r.filters ?? []).map((f: any, i: number) => ({ value: '', value2: '', scope: '', ...f, id: `${id}:read:${index}:filter:${i}`, logicalOperator: f.logicalOperator ?? f.logic ?? '' })),
      })),
      businessRules: {
        intraEntityRulesEnabled: false, interEntityRulesEnabled: false, globalEntityRulesEnabled: false,
        intraEntityRules: [], interEntityRules: [], globalEntityRules: [], operation: '', transformation: {},
        ...(rules && !Array.isArray(rules) ? rules : {}),
      },
      childEntities: (raw['childEntities'] ?? []).map((c: any, i: number) => ({ ...c, id: `${id}:child:${i}`, entityName: c.entityName ?? c.entity ?? '', linkAttribute: c.linkAttribute ?? '' })),
      permissions: raw['permissions'] ?? [],
    };
  }

  selectEntity(id: string): void { if (id !== this.selectedId) { this.selectedId = id; this.saveStatus = 'idle'; } }
  openAddEntity(): void { this.detail?.openAddModal(); }
  updateEntities(event: { entities: DeamEntity[]; selectedId: string | null }): void {
    this.entities = event.entities;
    this.selectedId = event.selectedId;
    for (const entity of this.entities) {
      if (!this.entitySources.has(entity.id)) this.entitySources.set(entity.id, { uuid: crypto.randomUUID(), definition: {}, saved: false });
    }
    this.refreshCards();
  }
  private refreshCards(): void {
    this.sidePanelItems = this.entities.map(entity => ({ id: entity.id, label: entity.displayName?.trim() || entity.entity, description: entity.entity, icon: entity.isBusinessEntity ? 'dataset' : 'table' }));
  }

  save(entity: DeamEntity): void {
    if (this.saveStatus === 'saving') return;
    const saveApiUrl = this.screenConfig?.components?.entityDetailAccordionComponent?.saveApiUrl;
    if (!saveApiUrl) { this.showToast('The save API URL is missing from the DEAM configuration.'); return; }
    const userId = String(this.keycloakService.getKeycloakInstance()?.tokenParsed?.['email'] || '').trim();
    if (!userId) { this.showToast('Unable to save: the logged-in user email is unavailable.'); return; }
    const source = this.entitySources.get(entity.id);
    if (!source) { this.showToast('Unable to save: entity UUID is missing.'); return; }
    const { id, ...fields } = structuredClone(entity);
    const definition = {
      ...source.definition, ...fields,
      attributesList: fields.attributesList.map(({ id: attrId, expanded, autoGenerated, ...attribute }) => ({ ...attribute, autogenerated: autoGenerated })),
      deamReads: fields.deamReads.map(({ id: readId, ...read }) => ({ ...read, filters: read.filters.map(({ id: filterId, ...filter }) => filter) })),
      childEntities: fields.childEntities.map(({ id: childId, ...child }) => child),
      permissions: fields.permissions.map(({ id: permissionId, ...permission }) => permission),
    };
    const request = {
      requestData: { payload: definition, uuid: source.uuid, businessKey: ['uuid'], operation: source.saved ? 'update' : 'insert' },
      userId, responseList: [], entity: 'entity_store', batchId: 'test', workflowId: 'ABC-123', endpoints: [], version: '1.0',
    };
    this.saveStatus = 'saving';
    this.http.post(this.resolveApiUrl(saveApiUrl), request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.entitySources.set(entity.id, { ...source, definition, saved: true });
        this.saveStatus = 'saved'; this.showToast(`${definition.entity} saved successfully`);
        this.refreshCards(); this.changeDetector.markForCheck();
      },
      error: () => { this.saveStatus = 'idle'; this.showToast(`Unable to save ${definition.entity}. Please try again.`); this.changeDetector.markForCheck(); },
    });
  }
  private resolveApiUrl(endpoint: string): string {
    return `${environment.URL.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
  }

  showToast(message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastMessage = message;
    this.toastVisible = true;
    this.changeDetector.markForCheck();
    this.toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.toastTimer = undefined;
      this.changeDetector.markForCheck();
    }, 3000);
  }

}
